import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MdAdd, MdDelete, MdArrowBack } from 'react-icons/md';
import { useProducts } from '../../context/ProductsContext';
import './Admin.css';

const CATEGORIES = ['women', 'men', 'kids', 'others'];
const COMMON_SIZES = ['Free Size', 'S', 'M', 'L', 'XL', 'XXL'];

const emptyProduct = {
    productId: '',
    productTitle: '',
    sku: '',
    category: 'women',
    productDesc: '',
    originalPrice: '',
    discountedPrice: '',
    discount: '',
    availableSize: [],
    inStock: true,
    stock: 0,
    material: '',
    care: '',
    features: [],
    specifications: {},
    images: [],
    videos: []
};

const AdminProductForm = () => {
    const { productId } = useParams();
    const isEditMode = Boolean(productId);
    const navigate = useNavigate();
    const { getProductById, saveProduct, loading } = useProducts();

    const [form, setForm] = useState(emptyProduct);
    const [newFeature, setNewFeature] = useState('');
    const [specKey, setSpecKey] = useState('');
    const [specValue, setSpecValue] = useState('');
    const [newImageUrl, setNewImageUrl] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isEditMode && !loading) {
            const existing = getProductById(productId);
            if (existing) {
                setForm({ ...emptyProduct, ...existing });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditMode, productId, loading]);

    const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

    const toggleSize = (size) => {
        setForm((prev) => {
            const has = prev.availableSize.includes(size);
            return {
                ...prev,
                availableSize: has
                    ? prev.availableSize.filter((s) => s !== size)
                    : [...prev.availableSize, size]
            };
        });
    };

    const addFeature = () => {
        if (!newFeature.trim()) return;
        updateField('features', [...form.features, newFeature.trim()]);
        setNewFeature('');
    };

    const removeFeature = (index) => {
        updateField('features', form.features.filter((_, i) => i !== index));
    };

    const addSpecification = () => {
        if (!specKey.trim() || !specValue.trim()) return;
        updateField('specifications', { ...form.specifications, [specKey.trim()]: specValue.trim() });
        setSpecKey('');
        setSpecValue('');
    };

    const removeSpecification = (key) => {
        const updated = { ...form.specifications };
        delete updated[key];
        updateField('specifications', updated);
    };

    const addImageUrl = () => {
        if (!newImageUrl.trim()) return;
        updateField('images', [...form.images, newImageUrl.trim()]);
        setNewImageUrl('');
    };

    const removeExistingImage = (url) => {
        updateField('images', form.images.filter((img) => img !== url));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!form.productId.trim()) {
            setError('Product ID is required (e.g. W004, M0015).');
            return;
        }
        if (!form.productTitle.trim()) {
            setError('Product title is required.');
            return;
        }

        setSaving(true);
        try {
            await saveProduct(form.productId, {
                ...form,
                images: form.images.length > 0 ? form.images : ['/No-Image-Placeholder.svg']
            });

            navigate('/admin');
        } catch (err) {
            setError('Failed to save product: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    let submitLabel = isEditMode ? 'Save Changes' : 'Add Product';
    if (saving) submitLabel = 'Saving...';

    return (
        <div className="admin-page">
            <button type="button" className="admin-back-btn" onClick={() => navigate('/admin')}>
                <MdArrowBack size={20} /> Back to Dashboard
            </button>

            <div className="admin-card">
                <h1>{isEditMode ? 'Edit Product' : 'Add New Product'}</h1>

                {error && <div className="admin-error">{error}</div>}

                <form onSubmit={handleSubmit} className="admin-form">
                    <div className="admin-form-row">
                        <label className="admin-field">
                            <span>Product ID *</span>
                            <input
                                type="text"
                                value={form.productId}
                                onChange={(e) => updateField('productId', e.target.value)}
                                disabled={isEditMode}
                                placeholder="e.g. W004"
                                required
                            />
                        </label>

                        <label className="admin-field">
                            <span>Category *</span>
                            <select
                                value={form.category}
                                onChange={(e) => updateField('category', e.target.value)}
                            >
                                {CATEGORIES.map((cat) => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </label>
                    </div>

                    <label className="admin-field">
                        <span>Product Title *</span>
                        <input
                            type="text"
                            value={form.productTitle}
                            onChange={(e) => updateField('productTitle', e.target.value)}
                            required
                        />
                    </label>

                    <div className="admin-form-row">
                        <label className="admin-field">
                            <span>SKU</span>
                            <input
                                type="text"
                                value={form.sku}
                                onChange={(e) => updateField('sku', e.target.value)}
                            />
                        </label>
                        <label className="admin-field">
                            <span>Material</span>
                            <input
                                type="text"
                                value={form.material}
                                onChange={(e) => updateField('material', e.target.value)}
                            />
                        </label>
                    </div>

                    <label className="admin-field">
                        <span>Description</span>
                        <textarea
                            rows={3}
                            value={form.productDesc}
                            onChange={(e) => updateField('productDesc', e.target.value)}
                        />
                    </label>

                    <div className="admin-form-row">
                        <label className="admin-field">
                            <span>Original Price (₹)</span>
                            <input
                                type="number"
                                value={form.originalPrice}
                                onChange={(e) => updateField('originalPrice', e.target.value)}
                            />
                        </label>
                        <label className="admin-field">
                            <span>Discounted Price (₹)</span>
                            <input
                                type="number"
                                value={form.discountedPrice}
                                onChange={(e) => updateField('discountedPrice', e.target.value)}
                            />
                        </label>
                        <label className="admin-field">
                            <span>Discount (%)</span>
                            <input
                                type="number"
                                value={form.discount}
                                onChange={(e) => updateField('discount', e.target.value)}
                            />
                        </label>
                    </div>

                    <div className="admin-form-row">
                        <label className="admin-field">
                            <span>Stock Quantity</span>
                            <input
                                type="number"
                                value={form.stock}
                                onChange={(e) => updateField('stock', Number.parseInt(e.target.value, 10) || 0)}
                            />
                        </label>
                        <label className="admin-field admin-field-checkbox">
                            <input
                                type="checkbox"
                                checked={form.inStock}
                                onChange={(e) => updateField('inStock', e.target.checked)}
                            />
                            <span>In Stock</span>
                        </label>
                    </div>

                    <label className="admin-field">
                        <span>Care Instructions</span>
                        <input
                            type="text"
                            value={form.care}
                            onChange={(e) => updateField('care', e.target.value)}
                        />
                    </label>

                    <div className="admin-field">
                        <span>Available Sizes</span>
                        <div className="admin-chip-row">
                            {COMMON_SIZES.map((size) => (
                                <button
                                    type="button"
                                    key={size}
                                    className={`admin-chip ${form.availableSize.includes(size) ? 'selected' : ''}`}
                                    onClick={() => toggleSize(size)}
                                >
                                    {size}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="admin-field">
                        <span>Features</span>
                        <ul className="admin-list">
                            {form.features.map((feature) => (
                                <li key={feature}>
                                    {feature}
                                    <button type="button" onClick={() => removeFeature(form.features.indexOf(feature))}>
                                        <MdDelete size={16} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                        <div className="admin-inline-add">
                            <input
                                type="text"
                                value={newFeature}
                                onChange={(e) => setNewFeature(e.target.value)}
                                placeholder="Add a feature"
                            />
                            <button type="button" onClick={addFeature}><MdAdd size={18} /></button>
                        </div>
                    </div>

                    <div className="admin-field">
                        <span>Specifications</span>
                        <ul className="admin-list">
                            {Object.entries(form.specifications).map(([key, value]) => (
                                <li key={key}>
                                    <strong>{key}:</strong> {value}
                                    <button type="button" onClick={() => removeSpecification(key)}>
                                        <MdDelete size={16} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                        <div className="admin-inline-add">
                            <input
                                type="text"
                                value={specKey}
                                onChange={(e) => setSpecKey(e.target.value)}
                                placeholder="Key (e.g. Fabric)"
                            />
                            <input
                                type="text"
                                value={specValue}
                                onChange={(e) => setSpecValue(e.target.value)}
                                placeholder="Value (e.g. Cotton)"
                            />
                            <button type="button" onClick={addSpecification}><MdAdd size={18} /></button>
                        </div>
                    </div>

                    <div className="admin-field">
                        <span>Images</span>
                        {form.images.length > 0 && (
                            <div className="admin-image-preview-row">
                                {form.images.map((url) => (
                                    <div key={url} className="admin-image-preview">
                                        <img src={url} alt="Product" />
                                        <button type="button" onClick={() => removeExistingImage(url)}>
                                            <MdDelete size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="admin-inline-add">
                            <input
                                type="text"
                                value={newImageUrl}
                                onChange={(e) => setNewImageUrl(e.target.value)}
                                placeholder="Paste an image URL (e.g. /Products/W004.jpg or https://...)"
                            />
                            <button type="button" onClick={addImageUrl}><MdAdd size={18} /></button>
                        </div>
                        <p className="admin-hint">Upload the image file to the public/Products folder (or an image host) first, then paste its URL here.</p>
                    </div>

                    <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
                        {submitLabel}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default AdminProductForm;
